import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ArrowLeftIcon from "assets/icons/ArrowLeftIcon";
import ArrowRightIcon from "assets/icons/ArrowRightIcon";
import FlowerIcon from "assets/icons/FlowerIcon";
import Container from "components/__reusable/Container";
import ProfileAvatar from "components/__reusable/ProfileAvatar";
import ClassYearDropdown from "components/PictureFilter/ClassYearDropdown";
import useAllClasses from "hooks/useAllClasses";
import { formatDate } from "utilities/DateFormatter";
import { SupportedLang, StudentSortValue } from "config/constants";
import type { Class } from "types/Class";
import type { SummarizationByStudent } from "types/Summarization";
import { getPublishedPictureSummarizationByStudents } from "services/summarizationServices";
import { getStudents } from "services/studentServices";
import { setErrorStatus } from "features/error/errorSlice";
import useAppDispatch from "hooks/useAppDispatch";

const TODAY = new Date();

const MonthlyPictureGraph = () => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [classYear, setClassYear] = useState<number>(0);
  const isMaxMonth = () =>
    currentDate.getMonth() >= TODAY.getMonth() &&
    currentDate.getFullYear() >= TODAY.getFullYear();
  const setDate = (step: number) => {
    const date = new Date(currentDate);
    date.setMonth(date.getMonth() + step);
    setCurrentDate(date);
  };

  // Calculate the class year based on fiscal year (April-March)
  const calculateClassYear = (date: Date) => {
    const month = date.getMonth(); // 0-based (January is 0, April is 3)
    const year = date.getFullYear();

    // If the current month is April or later (April = 3), the class year is the current year
    // If the current month is before April (January to March), the class year is the previous year
    return month >= 3 ? year : year - 1;
  };

  const { classes } = useAllClasses();
  const [currentClass, setCurrentClass] = useState<Class | undefined>(
    undefined,
  );
  // Filter classes by the calculated class year and sort them in Japanese aiueo order
  const filteredAndSortedClasses = classes
    .filter((c) => c.year === classYear)
    .sort((a, b) => a.name.localeCompare(b.name, "ja"));

  useEffect(() => {
    // Update current class when class year changes
    if (
      filteredAndSortedClasses.length > 0 &&
      (!currentClass || currentClass.year !== classYear)
    ) {
      // Set default to first class from sorted list when class year changes
      setCurrentClass(filteredAndSortedClasses[0]);
    }
  }, [classYear, classes, currentClass, filteredAndSortedClasses]);

  const [studentPictures, setStudentPictures] = useState<
    SummarizationByStudent[]
  >([]);
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  // Track when we need to refresh the student pictures
  const [shouldRefreshData, setShouldRefreshData] = useState(true);

  // When currentDate or currentClass changes, mark that we need to refresh
  useEffect(() => {
    if (currentClass) {
      setShouldRefreshData(true);
    }
  }, [currentClass, currentDate]);

  // Fetch student pictures only when shouldRefreshData is true
  const fetchStudentPictures = useCallback(async () => {
    if (!currentClass || !shouldRefreshData) return;

    setIsLoading(true);
    try {
      const startDate = new Date(currentDate);
      startDate.setDate(1);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + 1);
      endDate.setDate(0);
      endDate.setHours(23, 59, 59, 999);
      const studentPictures = await getPublishedPictureSummarizationByStudents(
        currentClass.id,
        startDate.toISOString(),
        endDate.toISOString(),
      );

      const students = await getStudents(
        currentClass.id,
        StudentSortValue.name_asc,
      );
      const summaries: SummarizationByStudent[] = [];
      for (let student of students) {
        const summary: SummarizationByStudent = {
          student_id: student.id,
          first_name: student.first_name,
          last_name: student.last_name,
          first_name_kata: student.first_name_kata,
          last_name_kata: student.last_name_kata,
          profile_picture: student.profile_picture_for_admin || "",
          total_pictures:
            studentPictures.total.find((s) => s.student_id === student.id)
              ?.number_tags || 0,
        };
        summaries.push(summary);
      }
      setStudentPictures(summaries);
      setShouldRefreshData(false); // Mark that we've refreshed the data
    } catch (e) {
      dispatch(setErrorStatus(""));
    } finally {
      setIsLoading(false);
    }
  }, [currentClass, currentDate, dispatch, shouldRefreshData]);

  useEffect(() => {
    fetchStudentPictures();
  }, [fetchStudentPictures]);

  // Update class year whenever the current date changes
  useEffect(() => {
    const newClassYear = calculateClassYear(currentDate);
    setClassYear(newClassYear);
  }, [currentDate]);

  const renderStudentGraph = () => {
    return studentPictures.map((s) => (
      <div
        key={s.student_id}
        className="w-full flex items-center gap-2 md:gap-3 py-1 md:py-2"
      >
        {/* Student Info Section */}
        <div className="flex items-center gap-2 min-w-0 flex-shrink-0 w-32 sm:w-40 md:w-48 lg:w-56">
          <div className="w-8 h-8 md:w-10 md:h-10 flex-shrink-0">
            <ProfileAvatar pictureUrl={s.profile_picture} />
          </div>
          <p className="text-xs md:text-sm truncate min-w-0">
            {`${s.last_name} ${s.first_name}`}
          </p>
        </div>

        {/* Count and Flower Section */}
        <div className="flex flex-col items-center justify-center w-12 md:w-16 flex-shrink-0">
          <p className="text-xs md:text-sm font-medium">{s.total_pictures}</p>
          <div
            className={`flex justify-center mt-1 ${
              s.total_pictures < 30 ? "hidden" : ""
            }`}
          >
            <div className="w-4 h-4 md:w-5 md:h-5">
              <FlowerIcon width={"100%"} height={"100%"} color={"#ff0000"} />
            </div>
          </div>
        </div>

        {/* Graph Section */}
        {s.total_pictures > 0 && (
          <div className="flex-1 relative min-w-0 h-6 md:h-8">
            <div
              className="h-1/2 absolute z-10 bg-primary top-1/2 -translate-y-1/2 min-w-[2px]"
              style={{
                width:
                  s.total_pictures > 100
                    ? "100%"
                    : `${Math.max(s.total_pictures, 2)}%`,
              }}
            ></div>
            <div className="w-full h-full flex relative">
              <div className="w-[30%] border-dashed border-r border-r-gray-400 h-full"></div>
              <div className="flex-1 relative">
                <div className="absolute right-[2ch] w-px border-dashed border-r border-r-gray-400 h-full"></div>
              </div>
            </div>
          </div>
        )}
      </div>
    ));
  };

  return (
    <div className="h-full flex flex-col gap-3 lg:gap-5 p-2 md:p-4">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-lg md:text-xl font-semibold leading-tight">
          {t("_published_picture._number_published_picture")}
        </h2>
        <div className="w-full sm:w-auto min-w-[200px] md:min-w-[250px] bg-white p-2 md:p-3 rounded border border-[#9B9B9B] flex justify-between items-center">
          <button
            onClick={() => setDate(-1)}
            className="rounded p-1 transition-colors hover:bg-gray-200 active:bg-gray-300"
          >
            <ArrowLeftIcon width={24} height={24} color={"#9B9B9B"} />
          </button>
          <p className="text-base md:text-xl font-medium px-2 text-center">
            {formatDate(currentDate, {
              [SupportedLang.ja]: "yo Mo",
              [SupportedLang.en]: "yyyy MMMM",
            })}
          </p>
          <button
            className={`rounded p-1 transition-colors hover:bg-gray-200 active:bg-gray-300 ${
              isMaxMonth() && "invisible"
            }`}
            onClick={() => setDate(1)}
          >
            <ArrowRightIcon width={24} height={24} color={"#9B9B9B"} />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 min-h-0 relative">
        <Container>
          <div className="w-full h-full flex flex-col gap-2 md:gap-4">
            {/* Class Dropdown */}
            <div className="w-full">
              <ClassYearDropdown
                classes={classes}
                currentClass={currentClass}
                onChange={(classId) => {
                  const selectedClass = classes.find((c) => c.id === classId);
                  if (selectedClass) setCurrentClass(selectedClass);
                }}
                classYear={classYear}
                className="rounded-md shadow-sm w-full"
              />
            </div>

            {/* Graph Header */}
            <div className="w-full flex items-center gap-2 md:gap-3 border-b border-gray-200">
              <div className="w-32 sm:w-40 md:w-48 lg:w-56 flex-shrink-0"></div>
              <div className="w-12 md:w-16 flex-shrink-0"></div>
              <div className="flex-1 flex relative text-xs md:text-sm text-gray-600 h-6">
                <div className="absolute left-[30%] -translate-x-1/2">30</div>
                <div className="absolute right-0 -translate-x-1/2">100</div>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 min-h-0">
              {isLoading && (
                <div className="flex items-center justify-center h-32">
                  <p className="text-gray-500">Loading...</p>
                </div>
              )}
              {!isLoading && studentPictures.length === 0 && (
                <div className="flex items-center justify-center h-32">
                  <p className="text-gray-500">{t("_generic._no_data")}</p>
                </div>
              )}
              {!isLoading && studentPictures.length > 0 && (
                <div className="h-full max-h-[calc(100vh-250px)] overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-gray-400">
                  <div className="space-y-1">{renderStudentGraph()}</div>
                </div>
              )}
            </div>
          </div>
        </Container>
        {/* Scrollbar positioned outside container */}
        <div className="absolute right-0 top-0 w-1 h-full pointer-events-none"></div>
      </div>
    </div>
  );
};

export default MonthlyPictureGraph;
